import React from 'react';
import { ShieldAlert, ShieldCheck, Info, Loader2 } from 'lucide-react';
import { configuredRestrictionsFrom, configuredAllergenFamiliesFrom } from '../../../../shared/allergenReference.js';
import { categoryCoverageFor } from '../../../../shared/dishCompatibility.js';

/** Un plat tel que le catalogue le renvoie — seuls les champs utiles à la compatibilité sont typés ici. */
export interface CoverageDish {
    id: string;
    name: string;
    category?: string;
    ingredients?: any[];
    dietary_tags?: string[];
}

export interface AllergenTerm { family: string; term: string; disabled?: boolean; kind?: string; group_kind?: string | null }

type Verdict = 'ok' | 'adaptable' | 'aucun';

interface ProfileRow { label: string; kind: 'restriction' | 'allergie'; worst: Verdict; blockedCategories: string[] }

const VERDICT_ORDER: Record<Verdict, number> = { aucun: 0, adaptable: 1, ok: 2 };

/**
 * Panneau « Couverture des régimes » de l'écran de configuration du menu : pour chaque régime qu'un convive
 * peut déclarer, dit s'il lui restera au moins un plat dans chaque catégorie.
 *
 * Pourquoi : un convive sans lactose s'est retrouvé sans aucun plat principal, et l'établissement ne l'a
 * découvert qu'une fois les invitations parties, convive par convive. Signalé par Emeni (2026-08-24) :
 * « il est impossible pour le convive d'avoir une alternative ».
 *
 * Le calcul réutilise exactement la règle qui s'applique sur la page du convive
 * (shared/dishCompatibility.js) : ce qui est annoncé ici est ce que le convive verra, toujours.
 * Volontairement un simple avertissement — il n'empêche jamais d'envoyer les invitations, un menu imposé ou
 * un événement sans convive concerné restant des cas légitimes. Choix d'Emeni (2026-08-24).
 */
export function RegimeCoveragePanel({ dishesByCategory, allergenTerms, loading }: {
    dishesByCategory: Record<string, CoverageDish[]>;
    allergenTerms: AllergenTerm[] | null;
    loading: boolean;
}) {
    const [showAll, setShowAll] = React.useState(false);

    const rows = React.useMemo<ProfileRow[]>(() => {
        const categories = Object.entries(dishesByCategory);
        if (!categories.length) return [];
        const terms = allergenTerms || [];
        const profiles: { label: string; kind: 'restriction' | 'allergie' }[] = [
            ...configuredRestrictionsFrom(terms).map((label: string) => ({ label, kind: 'restriction' as const })),
            ...configuredAllergenFamiliesFrom(terms).map((label: string) => ({ label, kind: 'allergie' as const })),
        ];
        return profiles.map(({ label, kind }) => {
            // Une restriction se coche dans la liste des restrictions, une allergie se saisit dans la liste
            // des ingrédients à éviter : les deux ne passent pas par le même chemin de détection, il faut
            // donc les tester chacune dans son propre champ pour refléter la réalité.
            const profile = kind === 'restriction'
                ? { restrictions: [label], allergies: [], customTerms: terms }
                : { restrictions: [], allergies: [label], customTerms: terms };
            let worst: Verdict = 'ok';
            const blockedCategories: string[] = [];
            categories.forEach(([category, dishes]) => {
                const verdict = categoryCoverageFor(dishes, profile).verdict as Verdict;
                if (VERDICT_ORDER[verdict] < VERDICT_ORDER[worst]) worst = verdict;
                if (verdict === 'aucun') blockedCategories.push(category);
            });
            return { label, kind, worst, blockedCategories };
        }).sort((a, b) => VERDICT_ORDER[a.worst] - VERDICT_ORDER[b.worst] || a.label.localeCompare(b.label, 'fr'));
    }, [dishesByCategory, allergenTerms]);

    if (!Object.keys(dishesByCategory).length) return null;

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-xs text-content-muted">
                <Loader2 size={13} className="animate-spin" /> Analyse de la couverture des régimes…
            </div>
        );
    }

    const blocked = rows.filter((r) => r.worst === 'aucun');
    const adaptableOnly = rows.filter((r) => r.worst === 'adaptable');
    // Par défaut on ne montre que ce qui pose problème : sur une vingtaine de régimes testés, une liste
    // intégralement verte n'apprend rien et noie les deux lignes qui comptent.
    const visible = showAll ? rows : [...blocked, ...adaptableOnly];

    return (
        <div className="rounded-xl border border-border bg-surface-soft p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="text-[11px] font-black text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                    {blocked.length > 0
                        ? <ShieldAlert size={13} className="text-red-500" />
                        : <ShieldCheck size={13} className="text-brand-600 dark:text-brand-400" />}
                    Couverture des régimes
                </h4>
                <button type="button" onClick={() => setShowAll((v) => !v)}
                    className="text-[10px] font-bold uppercase tracking-wide text-content-muted hover:text-content">
                    {showAll ? 'Ne montrer que les problèmes' : `Tout afficher (${rows.length})`}
                </button>
            </div>

            {blocked.length === 0 && adaptableOnly.length === 0 && !showAll && (
                <p className="text-[11px] text-content-muted">
                    Chaque régime testé garde au moins un plat par catégorie.
                </p>
            )}

            {visible.length > 0 && (
                <div className="space-y-1">
                    {visible.map((r) => (
                        <div key={`${r.kind}-${r.label}`}
                            className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-[11px] ${
                                r.worst === 'aucun' ? 'bg-red-500/10 text-red-700 dark:text-red-400'
                                    : r.worst === 'adaptable' ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                                        : 'text-content-muted'}`}>
                            <span className="font-bold shrink-0">{r.label}</span>
                            <span className="text-content-muted">·</span>
                            <span>
                                {r.worst === 'aucun'
                                    ? <>aucun plat possible en {r.blockedCategories.join(', ').toLowerCase()}</>
                                    : r.worst === 'adaptable'
                                        ? <>plats disponibles uniquement en retirant un ingrédient</>
                                        : <>au moins un plat convient dans chaque catégorie</>}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {blocked.length > 0 && (
                <p className="flex items-start gap-1.5 text-[10px] text-content-muted pt-1">
                    <Info size={11} className="shrink-0 mt-0.5" />
                    <span>
                        Un convive concerné n'aura rien à choisir dans ces catégories. Vous pouvez marquer
                        l'accompagnement ou la sauce en cause comme « possible de retirer » sur la fiche du plat,
                        ajouter un plat à la formule, ou laisser tel quel — l'envoi des invitations reste possible.
                    </span>
                </p>
            )}
        </div>
    );
}
